import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './primitives';

// =============================================================================
// Widget size
// =============================================================================

export const WidgetSizeSchema = z.enum(['small', 'medium', 'large']);
export type WidgetSize = z.infer<typeof WidgetSizeSchema>;

// =============================================================================
// Known core widget keys (addon widgets layer on top as plain strings)
// =============================================================================

export const CoreWidgetKeySchema = z.enum([
  'my_tasks',
  'upcoming_events',
  'inbox_summary',
  'recent_docs',
]);
export type CoreWidgetKey = z.infer<typeof CoreWidgetKeySchema>;

/** widget_key is a plain string to allow addon-provided keys. */
export const WidgetKeySchema = z.string().min(1).max(100);

// =============================================================================
// Row + inputs
// =============================================================================

export const HomeWidgetConfigSchema = z.object({
  user_id: UuidSchema,
  tenant_id: UuidSchema,
  widget_key: WidgetKeySchema,
  position: z.number().int().nonnegative(),
  size: WidgetSizeSchema,
  config: z.record(z.unknown()),
  enabled: z.boolean(),
  updated_at: TimestampSchema,
});
export type HomeWidgetConfig = z.infer<typeof HomeWidgetConfigSchema>;

/** Single-row update (PATCH /home/widgets/:widget_key). */
export const HomeWidgetUpdateInputSchema = z
  .object({
    position: z.number().int().nonnegative(),
    size: WidgetSizeSchema,
    config: z.record(z.unknown()),
    enabled: z.boolean(),
  })
  .partial();
export type HomeWidgetUpdateInput = z.infer<typeof HomeWidgetUpdateInputSchema>;

/** Bulk replace (PUT /home/widgets) — used for drag-reorder. */
export const HomeWidgetBulkReplaceInputSchema = z.object({
  widgets: z
    .array(
      z.object({
        widget_key: WidgetKeySchema,
        position: z.number().int().nonnegative(),
        size: WidgetSizeSchema.default('medium'),
        config: z.record(z.unknown()).optional(),
        enabled: z.boolean().default(true),
      }),
    )
    .max(50),
});
export type HomeWidgetBulkReplaceInput = z.infer<typeof HomeWidgetBulkReplaceInputSchema>;
